import { User, UserArpMediaStatistics } from '@arpeggi/shared'
import { styled } from '@arpeggi/ui'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useUser } from '../../queries/users'
import AuthStore from '../../stores/AuthStore'
import Button from '../core/Button/Button'
import CenteredLoadingAnimation from '../core/CenteredLoadingAnimation'
import { Box, Column, Row, VerticalCenteredRow } from '../core/Components'
import { OpenseaLogo, SoundCloudIcon, TwitterLogo, VerifiedIcon } from '../core/icons'
import { EditProfileFlowModal } from '../EditProfileFlowModal/EditProfileFlowModal'
import ProfileAvatar from '../platform/explore/common/ProfileAvatar'

const ProfileSocialIcon = styled('img', {
  height: '15px',
  transition: '150ms',
  '&:hover': {
    opacity: '50%'
  }
})

const ProfilePicture = styled(ProfileAvatar, {
  width: '128px',
  height: '128px',
  borderRadius: '50%',
  marginTop: '-65px',
  boxShadow: '0px 1px 30px -2px rgba(0, 0, 0, 0.2)'
})

const DisplayName = styled('h2', {
  fontWeight: '700',
  fontSize: '28px',
  lineHeight: '28px',
  letterSpacing: '-0.02em',
  margin: '0'
})

const ProfileBio = styled('div', {
  fontSize: '16px',
  lineHeight: '20px',
  marginBottom: '16px'
})

type ProfileDataProps = Pick<User, 'twitterHandle'>
const TwitterVerified: FC<ProfileDataProps> = ({ twitterHandle }) => (
  <>
    {twitterHandle && (
      <VerifiedIcon
        style={{ height: '14px', width: '14px', marginBottom: '14px', marginLeft: '4px' }}
      />
    )}
  </>
)

interface EditProfileButtonProps {
  walletAddress: string
}
const EditProfileButton: FC<EditProfileButtonProps> = observer(({ walletAddress }) => {
  const { user } = AuthStore
  const { isLoggedIn } = useAuth()
  return (
    <>
      {isLoggedIn && user?.wallet_address === walletAddress && (
        <EditProfileFlowModal
          trigger={
            <Button type="outlined" rounded css={{ position: 'absolute', top: 18, right: '24px' }}>
              Edit Profile
            </Button>
          }
        />
      )}
    </>
  )
})

type SocialIconProps = Pick<User, 'twitterHandle' | 'openSeaHandle' | 'soundCloudHandle'>
const SocialIcons: FC<SocialIconProps> = ({ twitterHandle, openSeaHandle, soundCloudHandle }) => (
  <Row css={{ marginLeft: '16px', gap: '8px' }}>
    {twitterHandle && (
      <a href={`https://twitter.com/${twitterHandle}`}>
        <TwitterLogo style={{ width: '20px', height: '20px', color: '#1D9BF0' }} />
      </a>
    )}
    {openSeaHandle && (
      <a href={`https://opensea.io/${openSeaHandle}`}>
        <OpenseaLogo style={{ width: '20px', height: '20px' }} />
      </a>
    )}
    {soundCloudHandle && (
      <a href={`https://soundcloud.com/${soundCloudHandle}`}>
        <SoundCloudIcon style={{ width: '20px', height: '20px' }} />
      </a>
    )}
  </Row>
)

const Stat = styled('div', {
  fontSize: '12px',
  lineHeight: '12px',
  '@mobile': {
    fontSize: '16px',
    lineHeight: '16px'
  },
  color: '$gray05'
})

const Stats: FC<{ stats: UserArpMediaStatistics }> = ({ stats }) => (
  <Row css={{ gap: '16px', marginBottom: '16px' }}>
    <Stat>
      <span style={{ color: '#000000', fontWeight: 700 }}>{stats.songMintCount}</span>
      {' songs minted'}
    </Stat>
    <Stat>
      <span style={{ color: '#000000', fontWeight: 700 }}>
        {stats.sampleMintCount + stats.stemMintCount}
      </span>
      {' sounds minted'}
    </Stat>
    <Stat>
      <span style={{ color: '#000000', fontWeight: 700 }}>
        {stats.stemRemixCount + stats.sampleRemixCount}
      </span>
      {' features'}
    </Stat>
  </Row>
)

const ProfileInfo = observer(() => {
  const { wallet_address: walletAddress } = useParams<{ wallet_address: string }>()
  const { data, isLoading } = useUser({
    walletAddressOrId: walletAddress,
    parts: ['arpMediaStatistics']
  })

  return (
    <Column>
      <ProfilePicture eth={walletAddress} />
      <EditProfileButton walletAddress={walletAddress} />
      <VerticalCenteredRow
        css={{
          flexDirection: 'column',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          paddingTop: '18px',
          paddingBottom: '25px',
          '@tablet': {
            flexDirection: 'row',
            alignItems: 'center'
          }
        }}
      >
        {/* Bio/User Info */}
        {isLoading ? (
          <CenteredLoadingAnimation />
        ) : (
          <>
            <Box>
              <Row css={{ alignItems: 'center', marginBottom: '16px' }}>
                <DisplayName>{data.displayName ?? ''}</DisplayName>
                <TwitterVerified twitterHandle={data.twitterHandle} />
                <SocialIcons
                  twitterHandle={data.twitterHandle}
                  openSeaHandle={data.openSeaHandle}
                  soundCloudHandle={data.soundCloudHandle}
                />
              </Row>
              <Stats stats={data.arpMediaStatistics} />
              {data.bio && <ProfileBio>{data.bio}</ProfileBio>}
            </Box>
          </>
        )}
      </VerticalCenteredRow>
    </Column>
  )
})

export default ProfileInfo
